import { useSearchParams } from 'react-router-dom';
import { Badge, Tabs } from '@dreamtree-org/twreact-ui';
import { PageHeader } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import BrandingTab from './settings/BrandingTab';
import DomainsTab from './settings/DomainsTab';
import LocationsTab from './settings/LocationsTab';
import StaffTab from './settings/StaffTab';
import PoliciesTab from './settings/PoliciesTab';
import ProductTypesTab from './settings/ProductTypesTab';
import CustomFieldsTab from './settings/CustomFieldsTab';
import FeaturesTab from './settings/FeaturesTab';
import EmailTab from './settings/EmailTab';
import { tabCompleteness } from '../lib/settings';

const TABS = [
  { key: 'branding', label: 'Branding', Panel: BrandingTab },
  { key: 'catalogue', label: 'What you sell', Panel: ProductTypesTab },
  // The store's own spec fields per type (ADR 0009 §4, MERCH-86).
  { key: 'custom_fields', label: 'Custom fields', Panel: CustomFieldsTab },
  // D101: repairs and buy-back on or off (store:features to change, store:view to see).
  { key: 'features', label: 'Features', Panel: FeaturesTab },
  { key: 'domains', label: 'Domains', Panel: DomainsTab },
  { key: 'locations', label: 'Locations & hours', Panel: LocationsTab },
  { key: 'staff', label: 'Staff & roles', Panel: StaffTab },
  { key: 'policies', label: 'Policies', Panel: PoliciesTab },
  // MERCH-107 (D117): the store's own SMTP. Owner only (store:email); hidden from everyone else.
  { key: 'email', label: 'Email', Panel: EmailTab, can: ['store', 'email'] },
];

/** A tab's setup state from the store checklist (MERCH-58): nothing for tabs it does not cover. */
function TabState({ state }) {
  if (!state) return null;
  if (state.done) return <Badge size="xs" variant="success">Done</Badge>;
  return <Badge size="xs" variant={state.optional ? 'outline' : 'warning'}>{state.optional ? 'Optional' : 'To do'}</Badge>;
}

/** SELL-08 — store and staff setup. The tab is kept in ?tab= so it can be linked. */
export default function StoreSettings({ store }) {
  const [params, setParams] = useSearchParams();
  const can = useCan();
  const checklist = useQuery('merchantSetupChecklist', {}, { select: (r) => r.data, enabled: can('store', 'view') });
  const states = tabCompleteness(checklist.data && checklist.data.items);
  const tabs = TABS.filter((t) => !t.can || can(...t.can));
  const index = Math.max(0, tabs.findIndex((t) => t.key === params.get('tab')));
  const Active = tabs[index].Panel;
  return (
    <div className="space-y-4">
      <PageHeader title="Store settings" subtitle={(store && store.name) || undefined} />
      <div className="overflow-x-auto -mx-1 px-1">
        <Tabs index={index} onChange={(i) => { setParams({ tab: tabs[i].key }, { replace: true }); checklist.reload(); }} variant="line" orientation="horizontal">
          <Tabs.List className="min-w-max border-b border-[var(--app-border)]">
            {tabs.map((t) => (
              <Tabs.Tab key={t.key} tabClassName="min-h-[44px] whitespace-nowrap">
                <span className="inline-flex items-center gap-2">{t.label}<TabState state={states[t.key]} /></span>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </div>
      <Active store={store} />
    </div>
  );
}
